import { useMemo } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import LayoutContainer from 'components/Common/LayoutContainer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { OFFER_TYPE_BUNDLE_AND_SAVE_SLUG } from 'constants/offer'
import useIsAvailableForPurchase from '../hooks/useIsAvailableForPurchase'
import FormatCurrency from 'components/Common/FormatCurrency'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import type { BundleOfferPackageOptionView } from 'selectors/offerPage/bundledOfferPageSelectors'
import {
  getAvailableBundlePackages,
  areBundlePricesLoading,
} from 'selectors/offerPage/bundledOfferPageSelectors'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import TextButton from 'LuxKit/Button/TextButton'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { capitalise } from 'lib/string/stringUtils'
import { min, sum } from 'lib/array/arrayUtils'
import Group from 'components/utils/Group'
import DesktopRelatedTravelItems from '../RelatedTravelItems/DesktopRelatedTravelItems'
import { rem } from 'polished'
import Pane from 'components/Common/Pane'

const StyledLayoutContainer = styled(LayoutContainer)`
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  padding-top: ${rem(12)};
  padding-bottom: ${rem(12)};
`

const PriceSummary = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;

  ${mediaQueryUp.tablet} {
    text-align: right;
  }
`

const HiddenTablet = styled.span`
  display: none;

  ${mediaQueryUp.desktop} {
    display: inline;
  }
`

interface Props {
  packageOptions: { [bundlePackageId: string]: BundleOfferPackageOptionView }
  onViewPackageOptions: () => void
  offer?: App.BundleOffer
  isHeadlessMode: boolean
  isCalendarLoading?: boolean
}

function BundleAndSaveHeader(props: Props) {
  const intl = useIntl()
  const {
    onViewPackageOptions,
    packageOptions,
    isHeadlessMode,
    offer,
    isCalendarLoading,
  } = props

  const availableForPurchase = useIsAvailableForPurchase()

  const packageView = useMemo((): BundleOfferPackageOptionView => {
    const minPackageView = min(Object.values(packageOptions).filter((o) => o.capacityValid && o.hotelAvailable), (o) => o.hotelPrice)
    return minPackageView ?? Object.values(packageOptions)[0]
  }, [packageOptions])

  const {
    hotelPrice,
    propertyFees = 0,
  } = packageView

  const allPackages = useMemo(() => {
    return (packageView?.options ?? []).map(p => p.package)
  }, [packageView])

  const duration = useMemo(() => {
    return sum(allPackages, (p) => p.duration)
  }, [allPackages])

  const showPrices = packageView.capacityValid && packageView.hotelAvailable
  const durationLabel = intl.formatMessage(
    {
      id: 'k2lnr1mw5clExott1fED',
      defaultMessage: '{nights, plural, one {# night} other {# nights}}, 2 hotels from',
      description: 'Duration label showing nights and hotel count for bundle offer header',
    },
    { nights: duration },
  )
  const totalPrice = Math.ceil(hotelPrice + (propertyFees ?? 0))

  return (<Pane>
    <StyledLayoutContainer>
      <Group fullWidth direction="horizontal" verticalAlign="center" horizontalAlign="space-between" gap={16}>
        {!isHeadlessMode && <DesktopRelatedTravelItems offer={offer} />}
        <Group fullWidth direction="horizontal" verticalAlign="center" horizontalAlign="space-between" tabletHorizontalAlign="end" gap={16}>
          <PriceSummary>
            <LoadingIndicator visible={isCalendarLoading} floating opaque />
            {!availableForPurchase && <BodyText variant="large" weight="bold">
              <FormattedMessage
                id="H3uGIJ4XJl1xL_2TbAlf"
                defaultMessage="This offer has{mobileBreak} SOLD OUT"
                description="Status message when bundle offer has sold out on the offer detail page"
                values={{ mobileBreak: <CSSBreakpoint as="br" max="mobile" /> }}
              />
            </BodyText>}
            {availableForPurchase && showPrices && <>
              <Caption variant="medium" wrap="no-wrap">{capitalise(durationLabel.toLowerCase())}</Caption>
              <div>
                <Heading as="span" variant="heading6">
                  <FormatCurrency value={totalPrice} />
                </Heading>
                <Caption variant="medium" as="span">{intl.formatMessage({
                  id: '1qvoo2fJBFHDrVeP-rPV',
                  defaultMessage: '/bundle',
                  description: 'Price unit suffix for bundle pricing in header on the offer detail page',
                })}
                </Caption>
              </div>
            </>}
          </PriceSummary>
          {!availableForPurchase && <TextButton kind="primary" to={`/${OFFER_TYPE_BUNDLE_AND_SAVE_SLUG}`}>
            {intl.formatMessage({
              id: 'K-pdt7mCKuMJOrIZpRoi',
              defaultMessage: 'Browse all lux bundles',
              description: 'Link text to browse all bundle offers when current offer is sold out',
            })}
          </TextButton>}
          {availableForPurchase &&
            <TextButton kind="primary" onClick={onViewPackageOptions}>
              <FormattedMessage
                id="jHtd34aL4g-REc4tI7H7"
                defaultMessage="View{nbsp}<hidden>bundle{nbsp}</hidden>options"
                description="Button text to view bundle options with responsive layout in header"
                values={{
                  nbsp: '\u00A0',
                  hidden: (chunks: React.ReactNode) => <HiddenTablet>{chunks}</HiddenTablet>,
                }}
              />
            </TextButton>}
        </Group>
      </Group>
    </StyledLayoutContainer>
  </Pane>)
}

export default withOfferPageAndReduxState((state, appState) => ({
  packageOptions: getAvailableBundlePackages(state, appState),
  offer: state.offer,
  isCalendarLoading: areBundlePricesLoading(appState, state),
  isHeadlessMode: appState.system.headlessMode,
}), BundleAndSaveHeader)
